import { Badge } from "@/components/ui/badge";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CardWithHeader } from "@/components/card-with-header";
import { DeactivateReactivateUserMenuItem } from "@/routes/(app)/_main-layout/_settings/-components/deactivate-reactivate-user-menu-item";
import {
  CreateUserModalButton,
  UserModalDialogContent,
} from "@/routes/(app)/_main-layout/_settings/-components/user-modal";
import { hasRole } from "@/lib/auth";
import { VolumeApi } from "@/lib/volume-api";
import { MerchantRole } from "@/types/user-management";
import { createFileRoute, redirect } from "@tanstack/react-router";
import clsx from "clsx";
import { Ellipsis, Settings } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute(
  "/(app)/_main-layout/_settings/settings/users",
)({
  component: RouteComponent,
  beforeLoad({ context }) {
    if (
      !hasRole("EDIT_SETTINGS", context.authorities) &&
      !hasRole("ACCOUNT_ADMIN", context.authorities)
    ) {
      throw redirect({ to: "/settings/account-settings", search: true });
    }
  },
  loader: async ({ context }) => {
    const volumeApi = new VolumeApi(
      context.accessToken,
      context.selectedApplicationId,
    );
    const users = await volumeApi.getMerchantAccounts({
      merchantId: context.merchantId,
    });
    const allRoles = await volumeApi.getMerchantRoles({
      merchantId: context.merchantId,
    });
    return {
      users,
      allRoles,
    };
  },
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const { users, allRoles } = Route.useLoaderData();

  return (
    <CardWithHeader
      variant="exchange"
      title="Users"
      headerContent={
        <CreateUserModalButton
          accessToken={context.accessToken}
          merchantId={context.merchantId}
          allRoles={allRoles}
        />
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="min-w-[120px]">Status</TableHead>
            <TableHead>Permission Types</TableHead>
            <TableHead>Created Date</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users
            .sort(
              (a, b) =>
                new Date(b.createdDate).getTime() -
                new Date(a.createdDate).getTime(),
            )
            .map((user) => (
              <TableRow className="text-black" key={user.userId}>
                <TableCell className={clsx(!user.active && "text-black/50")}>
                  {user.name}
                </TableCell>
                <TableCell
                  className={clsx(
                    "text-slate-700",
                    !user.active && "text-slate-700/50",
                  )}
                >
                  {user.active ? "Active" : "Deactivated"}
                </TableCell>
                <TableCell>
                  <div
                    className={clsx(
                      "flex flex-wrap gap-1",
                      !user.active && "opacity-60",
                    )}
                  >
                    {user.permissions.map((role) => (
                      <Badge
                        key={role}
                        variant="secondary"
                        className="bg-purple-100 text-purple-800 hover:bg-purple-100"
                      >
                        {role}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell
                  className={clsx(
                    !user.active && "text-slate-700/50",
                    user.active && "text-slate-700",
                  )}
                >
                  {new Date(user.createdDate).toLocaleDateString()}
                </TableCell>
                {context.userId !== user.userId ? (
                  <TableCell>
                    <UserRowDropdown
                      active={user.active}
                      allRoles={allRoles}
                      rolesForUser={allRoles.filter((role) =>
                        user.permissions.includes(role.id),
                      )}
                      email={user.email}
                      name={user.name}
                      phoneNumber={user.phoneNumber}
                      accessToken={context.accessToken}
                      merchantId={context.merchantId}
                      userId={user.userId}
                    />
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </CardWithHeader>
  );
}

function UserRowDropdown({
  active,
  allRoles,
  rolesForUser,
  email,
  name,
  phoneNumber,
  accessToken,
  merchantId,
  userId,
}: {
  active: boolean;
  allRoles: MerchantRole[];
  rolesForUser: MerchantRole[];
  email: string;
  name: string;
  phoneNumber: string | null;
  accessToken: string;
  merchantId: string;
  userId: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu>
      <Dialog open={open} onOpenChange={setOpen}>
        <UserModalDialogContent
          modalType="edit"
          allRoles={allRoles}
          initialPermissions={rolesForUser}
          email={email}
          fullName={name}
          accessToken={accessToken}
          merchantId={merchantId}
          userId={userId}
          phoneNumber={phoneNumber}
          setOpen={setOpen}
        />
        <DropdownMenuTrigger>
          <Ellipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {active && (
            <DropdownMenuItem asChild className="m-0">
              <DialogTrigger className="flex items-center gap-2 text-slate-700 w-full">
                <Settings />
                Edit
              </DialogTrigger>
            </DropdownMenuItem>
          )}
          <DeactivateReactivateUserMenuItem active={active} userId={userId} />
        </DropdownMenuContent>
      </Dialog>
    </DropdownMenu>
  );
}
